import { useLazyContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { RedoDiscount } from "@redotech/redo-merchant-marketing-rpc/schema/discounts/discount-schemas";
import {
  DiscountGroupType,
  DiscountMinRequirementType,
  DiscountValueType,
  ExpirationType,
} from "@redotech/redo-model/discount";
import { useEffect, useMemo, useState } from "react";
import { BadgeRedoListItem } from "../../arbiter-components/list/redo-list";
import { TimeOfDay } from "../../arbiter-components/time/redo-time-picker";
import type { ProductSelection, ShopifySegment } from "../discount-selector";
import {
  combineDateTime,
  CustomerEligibility,
  DiscountsContext,
  getInitialSegments,
  INITIAL_CODE_NAME,
  INITIAL_END_TIME,
  INITIAL_START_TIME,
  ProductsContext,
  ShopifySegmentsContext,
} from "../discount-selector";

export function useDiscountForm(existingDiscount?: RedoDiscount) {
  const [discounts] = useLazyContext(DiscountsContext);
  const [productsContext] = useLazyContext(ProductsContext);
  const [shopifySegmentsLoad] = useLazyContext(ShopifySegmentsContext);
  const [discountName, setDiscountName] = useState(
    existingDiscount?.name || "",
  );

  const [codeGenerationStrategy, setCodeGenerationStrategy] =
    useState<DiscountGroupType>(
      existingDiscount?.codeGenerationStrategy.strategy ||
        DiscountGroupType.STATIC,
    );

  const [codeStrategyName, setCodeStrategyName] = useState(() => {
    if (!existingDiscount) {
      return INITIAL_CODE_NAME;
    }
    return existingDiscount.codeGenerationStrategy.code;
  });

  const [discountAmount, setDiscountAmount] = useState<number | undefined>(
    existingDiscount?.discountSettings.discountValueAmount || undefined,
  );

  const [discountValueType, setDiscountValueType] = useState<DiscountValueType>(
    existingDiscount?.discountSettings.discountValueType ||
      DiscountValueType.PERCENTAGE,
  );

  // Combination settings
  const [combineDiscountWith, setCombineDiscountWith] = useState<{
    orderDiscounts: boolean;
    productDiscounts: boolean;
    shippingDiscount: boolean;
  }>(() => {
    if (!existingDiscount) {
      return {
        orderDiscounts: false,
        productDiscounts: false,
        shippingDiscount: false,
      };
    } else {
      const existingCombinesWith =
        existingDiscount.discountSettings.combinesWith;
      return {
        orderDiscounts: existingCombinesWith.orderDiscounts || false,
        productDiscounts: existingCombinesWith.productDiscounts || false,
        shippingDiscount: existingCombinesWith.shippingDiscount || false,
      };
    }
  });

  // Item restrictions
  const [excludeOnSale, setExcludeOnSale] = useState(
    existingDiscount?.discountSettings.excludeItemsOnSale || false,
  );

  const [specifyCollections, setSpecifyCollections] = useState(() => {
    if (!existingDiscount) {
      return false;
    }
    return (
      !!existingDiscount.discountSettings.appliesToCollections &&
      !!existingDiscount.discountSettings.appliesToCollections.length
    );
  });

  const [specifyProducts, setSpecifyProducts] = useState(() => {
    if (!existingDiscount) return false;
    return !!existingDiscount.discountSettings.appliesToProducts?.length;
  });

  const [selectedProducts, setSelectedProducts] = useState<ProductSelection[]>(
    () => {
      if (!existingDiscount?.discountSettings.appliesToProducts) return [];

      return existingDiscount.discountSettings.appliesToProducts.map((id) => {
        const productFromContext = productsContext.value?.find(
          (p) => p.id === id,
        );
        return {
          id,
          title:
            productFromContext?.name ||
            `Product ${id.includes("gid://shopify/Product/") ? id.split("/").pop() : id}`,
        };
      });
    },
  );

  const [collectionIds, setCollectionIds] = useState<string[]>(
    existingDiscount?.discountSettings.appliesToCollections || [],
  );

  // Expiration
  const [expiration, setExpiration] = useState<RedoDiscount["expiration"]>(
    existingDiscount?.expiration || { expirationType: ExpirationType.NEVER },
  );

  // Minimum requirements
  const [minimumQuantity, setMinimumQuantity] = useState<number | undefined>(
    existingDiscount?.discountSettings.minimumRequirement?.type ===
      DiscountMinRequirementType.MINIMUM_QUANTITY
      ? existingDiscount.discountSettings.minimumRequirement.minimum
      : undefined,
  );

  const [minimumSubtotal, setMinimumSubtotal] = useState<number | undefined>(
    existingDiscount?.discountSettings.minimumRequirement?.type ===
      DiscountMinRequirementType.MINIMUM_SUBTOTAL
      ? existingDiscount.discountSettings.minimumRequirement.minimum
      : undefined,
  );

  // Customer eligibility
  const [customerEligibility, setCustomerEligibility] =
    useState<CustomerEligibility>(() => {
      if (!existingDiscount) return CustomerEligibility.ALL;

      if (existingDiscount.discountSettings.limitToCustomers?.length) {
        return CustomerEligibility.SPECIFIC;
      }
      if (existingDiscount.discountSettings.limitToSegments?.length) {
        return CustomerEligibility.SEGMENTS;
      }

      return CustomerEligibility.ALL;
    });

  const [selectedCustomerIdsString, setSelectedCustomerIdsString] =
    useState<string>(
      existingDiscount?.discountSettings.limitToCustomers?.join(", ") || "",
    );

  // Convert string to array for validation
  const selectedCustomerIds = useMemo(() => {
    return selectedCustomerIdsString
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);
  }, [selectedCustomerIdsString]);

  // Usage limits
  const [totalUsageLimit, setTotalUsageLimit] = useState<number | undefined>(
    existingDiscount?.discountSettings.totalUsageLimit || undefined,
  );

  const [appliesOncePerCustomer, setAppliesOncePerCustomer] = useState<boolean>(
    existingDiscount?.discountSettings.appliesOncePerCustomer || false,
  );

  // Shopify segments
  const [userSelectedShopifySegments, setUserSelectedShopifySegments] =
    useState<ShopifySegment[]>(() =>
      getInitialSegments(existingDiscount, shopifySegmentsLoad.value),
    );

  const [segmentsInitialized, setSegmentsInitialized] = useState(false);

  // Date/time
  const [startDate, setStartDate] = useState<Date | undefined>(
    existingDiscount?.discountSettings.startsAt ?? undefined,
  );

  const [startTime, setStartTime] = useState<TimeOfDay | null>(() => {
    if (existingDiscount?.discountSettings.startsAt) {
      const date = existingDiscount.discountSettings.startsAt;
      return { hours: date.getHours(), minutes: date.getMinutes() };
    }
    return INITIAL_START_TIME;
  });

  const [endDate, setEndDate] = useState<Date | undefined>(() => {
    if (!existingDiscount?.discountSettings.endsAt) {
      return undefined;
    }
    return existingDiscount.discountSettings.endsAt;
  });

  const [endTime, setEndTime] = useState<TimeOfDay | null>(() => {
    if (existingDiscount?.discountSettings.endsAt) {
      const date = existingDiscount.discountSettings.endsAt;
      return { hours: date.getHours(), minutes: date.getMinutes() };
    }
    return INITIAL_END_TIME;
  });

  // Effects
  useEffect(() => {
    if (
      existingDiscount?.discountSettings.limitToSegments &&
      shopifySegmentsLoad.value &&
      !segmentsInitialized
    ) {
      const initialSegments = getInitialSegments(
        existingDiscount,
        shopifySegmentsLoad.value,
      );
      setUserSelectedShopifySegments(initialSegments);
      setSegmentsInitialized(true);
    }
  }, [existingDiscount, shopifySegmentsLoad.value, segmentsInitialized]);

  useEffect(() => {
    if (
      codeGenerationStrategy === DiscountGroupType.STATIC &&
      [ExpirationType.EXPIRATION_DAYS].includes(expiration.expirationType)
    ) {
      setExpiration({ expirationType: ExpirationType.NEVER });
    }
  }, [codeGenerationStrategy, expiration.expirationType]);

  // Computed values
  const productIds = useMemo(
    () => selectedProducts.map((p) => p.id),
    [selectedProducts],
  );

  const fullStartDateTime = useMemo(
    () => combineDateTime(startDate, startTime),
    [startDate, startTime],
  );

  const fullEndDateTime = useMemo(
    () => combineDateTime(endDate, endTime),
    [endDate, endTime],
  );

  const ShopifySegmentOptions: BadgeRedoListItem<ShopifySegment>[] =
    useMemo(() => {
      if (!shopifySegmentsLoad.value) return [];
      return shopifySegmentsLoad.value.map((segment) => ({
        id: segment.id,
        value: segment,
        type: "badge" as const,
        badge: { text: segment.name },
      }));
    }, [shopifySegmentsLoad.value]);

  const selectedShopifySegmentOptions: BadgeRedoListItem<ShopifySegment>[] =
    useMemo(() => {
      return userSelectedShopifySegments.map((segment) => ({
        id: segment.id,
        value: segment,
        type: "badge" as const,
        badge: { text: segment.name },
      }));
    }, [userSelectedShopifySegments]);

  // Handlers
  const handleSetExcludeOnSale = useHandler((checked: boolean) => {
    setExcludeOnSale(checked);
    if (checked) {
      setSpecifyCollections(false);
      setSpecifyProducts(false);
    }
  });

  const handleSetSpecifyCollections = useHandler((checked: boolean) => {
    setSpecifyCollections(checked);
    if (checked) {
      setExcludeOnSale(false);
      setSpecifyProducts(false);
      // Clear products when switching to collections
      setSelectedProducts([]);
    }
  });

  const handleSetSpecifyProducts = useHandler((checked: boolean) => {
    setSpecifyProducts(checked);
    if (checked) {
      setExcludeOnSale(false);
      setSpecifyCollections(false);
      // Clear collections when switching to products
      setCollectionIds([]);
    }
  });

  const handleShopifySegmentChange = useHandler(
    (items: BadgeRedoListItem<ShopifySegment>[]) => {
      setUserSelectedShopifySegments(items.map((item) => item.value));
    },
  );

  const handleSetMinimumQuantity = useHandler((value: number | undefined) => {
    setMinimumQuantity(value);
    if (value && value > 0) {
      // Clear minimum subtotal when setting quantity
      setMinimumSubtotal(undefined);
    }
  });

  const handleSetMinimumSubtotal = useHandler((value: number | undefined) => {
    setMinimumSubtotal(value);
    if (value && value > 0) {
      // Clear minimum quantity when setting subtotal
      setMinimumQuantity(undefined);
    }
  });

  return {
    // State values
    discountName,
    setDiscountName,
    codeGenerationStrategy,
    setCodeGenerationStrategy,
    codeStrategyName,
    setCodeStrategyName,
    discountAmount,
    setDiscountAmount,
    discountValueType,
    setDiscountValueType,
    combineDiscountWith,
    setCombineDiscountWith,
    excludeOnSale,
    setExcludeOnSale,
    specifyCollections,
    setSpecifyCollections,
    specifyProducts,
    setSpecifyProducts,
    selectedProducts,
    setSelectedProducts,
    collectionIds,
    setCollectionIds,
    expiration,
    setExpiration,
    minimumQuantity,
    setMinimumQuantity,
    minimumSubtotal,
    setMinimumSubtotal,
    customerEligibility,
    setCustomerEligibility,
    selectedCustomerIds,
    selectedCustomerIdsString,
    setSelectedCustomerIdsString,
    totalUsageLimit,
    setTotalUsageLimit,
    appliesOncePerCustomer,
    setAppliesOncePerCustomer,
    userSelectedShopifySegments,
    setUserSelectedShopifySegments,
    startDate,
    setStartDate,
    startTime,
    setStartTime,
    endDate,
    setEndDate,
    endTime,
    setEndTime,

    // Computed values
    productIds,
    fullStartDateTime,
    fullEndDateTime,
    ShopifySegmentOptions,
    selectedShopifySegmentOptions,

    // Handlers
    handleSetExcludeOnSale,
    handleSetSpecifyCollections,
    handleSetSpecifyProducts,
    handleShopifySegmentChange,
    handleSetMinimumQuantity,
    handleSetMinimumSubtotal,

    // Context data
    discounts,

    // Constants for validation
    CustomerEligibility,
  };
}
